// ---------------------------------------------------------------------------
// The session, over REST (R13)
//
// Until R13 the page signed in through Adornis's socket login
// (@adornis/user-forms functionalLogin → loginWithFlow over BaseQL), and the
// socket's script wrote the session's key into the `baseQLSession` cookie
// itself, where any script on the page could read it. Now the page signs in
// over the routes R11a made (server/routes/auth.ts); the server sets that
// cookie HttpOnly, and the page only ever learns who is signed in. It opens no
// BaseQL socket at all (tests/client-session.test.ts): a socket's script and
// the HttpOnly cookie in one browser would be two sessions.
//
//   check()    GET /api/auth/me. 200 is the user, 401 is nobody. At the start
//              anything else is "unreachable": the sign-in page says so and
//              offers to try again. Later it changes nothing — a blip in the
//              network is not a sign-out. Two checks at once share a request.
//   signIn()   POST /api/auth/login {username, password}, as JSON. A refusal
//              is the server's own words: the same 401 for an unknown name and
//              a wrong password (it does not tell which accounts exist), 429
//              with the wait, 400, 500.
//   signOut()  POST /api/auth/logout {}, as JSON. The server clears the cookie
//              even when it could not end the session (500): signed out then
//              too, with its words as a warning. With no answer the cookie is
//              still there, so the page stays signed in and says so, rather
//              than show a sign-in that a reload would undo.
//   watch()    decision 7.16: a 401 from /api/* (not /api/auth/*) and the tab
//              coming back (focus, visibilitychange) ask /me again. A 401 is a
//              question, not the answer: /api/cloud-accounts/:id/token and the
//              cloud-storage routes answer 401 for a revoked Google or
//              Microsoft account while the session is fine.
//
// Whatever answers last does not win: a sign-in or sign-out makes every check
// that started before it stale, so a late /me cannot undo either.
//
// forgetSocketKey() removes what the page before R13 left in the browser: the
// socket kept the session's key in localStorage ("sessionID") as well, where
// any script on the page can read it, and that key still signs in over REST.
//
// No page and nothing from Adornis is imported, so the DOM suite loads it
// (tests/dom/session.dom.ts). fetch is looked up when it is called: watch()
// puts its own in front of the page's.
// ---------------------------------------------------------------------------

import { type ReadonlySignal, signal } from '@preact/signals-core';

export interface SessionUser {
  id: string;
  username: string | null;
  email: string | null;
}

export type SessionState =
  | { status: 'checking' }
  | { status: 'signed-in'; user: SessionUser }
  /** ended: the session was there and is over (signed out elsewhere, expired), not a first visit. */
  | { status: 'signed-out'; ended: boolean }
  | { status: 'unreachable' };

export type SignInResult = { ok: true } | { ok: false; error: string };
export type SignOutResult = { ok: true; warning?: string } | { ok: false; error: string };

export const ME_URL = '/api/auth/me';
export const SIGN_IN_URL = '/api/auth/login';
export const SIGN_OUT_URL = '/api/auth/logout';

/** When the server could not be reached at all. */
export const NO_CONNECTION = 'Could not reach SDR Agent. Check your connection and try again.';
/** When the server refused a sign-in without words of its own. */
export const SIGN_IN_FAILED = 'Sign-in failed. Please try again.';
/** When a sign-out did not reach the server: the cookie is still there. */
export const SIGN_OUT_FAILED = 'Could not sign out. Check your connection and try again.';

export interface Session {
  readonly state: ReadonlySignal<SessionState>;
  /** Asks the server who is signed in. */
  check(): Promise<SessionState>;
  signIn(username: string, password: string): Promise<SignInResult>;
  signOut(): Promise<SignOutResult>;
  /** Asks again on a 401 from /api/* and when the tab comes back; returns the function that stops it. */
  watch(): () => void;
}

/** Where BaseQL's websocket transport kept the session's key (websocketTransport.js). */
export const SOCKET_KEY = 'sessionID';

/** Removes the session key the BaseQL socket kept in localStorage; storage that is refused is left alone. */
export function forgetSocketKey(): void {
  try {
    localStorage.removeItem(SOCKET_KEY);
  } catch {
    /* no storage, nothing kept */
  }
}

export interface SessionOptions {
  /** The window whose focus and address count; the page's by default. */
  win?: Window;
}

const JSON_HEADERS = { 'content-type': 'application/json', accept: 'application/json' };

/** A JSON body, or null for none. */
async function bodyOf(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const body: unknown = await res.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The user in a body from the auth routes, or null when it is not one. */
function userOf(body: Record<string, unknown> | null): SessionUser | null {
  const user = body?.ok === true ? body.user : null;
  if (!user || typeof user !== 'object') return null;
  const { id, username, email } = user as Record<string, unknown>;
  if (typeof id !== 'string' || !id) return null;
  return {
    id,
    username: typeof username === 'string' ? username : null,
    email: typeof email === 'string' ? email : null,
  };
}

const wordsOf = (body: Record<string, unknown> | null): string | null =>
  typeof body?.error === 'string' && body.error.trim() ? body.error : null;

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

export function createSession(options: SessionOptions = {}): Session {
  const win = () => options.win ?? window;
  const state = signal<SessionState>({ status: 'checking' });
  /** Bumped by every sign-in and sign-out: a check that started before one is stale. */
  let epoch = 0;
  let inFlight: Promise<SessionState> | null = null;

  /** An address the session watches: this origin's /api/*, but not the sign-in routes themselves. */
  const watched = (input: RequestInfo | URL): boolean => {
    try {
      const url = new URL(urlOf(input), win().location.href);
      return (
        url.origin === win().location.origin &&
        url.pathname.startsWith('/api/') &&
        !url.pathname.startsWith('/api/auth/')
      );
    } catch {
      return false;
    }
  };

  async function ask(): Promise<SessionState> {
    const started = epoch;
    let next: SessionState | null;
    try {
      const res = await fetch(ME_URL, { method: 'GET', headers: { accept: 'application/json' } });
      const user = res.ok ? userOf(await bodyOf(res)) : null;
      if (user) next = { status: 'signed-in', user };
      else if (res.status === 401) next = { status: 'signed-out', ended: state.peek().status === 'signed-in' };
      else next = null;
    } catch {
      next = null;
    }
    if (started !== epoch) return state.peek();
    // No answer to go by: only the very first question becomes "unreachable".
    if (!next) {
      if (state.peek().status === 'checking') state.value = { status: 'unreachable' };
      return state.peek();
    }
    state.value = next;
    return next;
  }

  function check(): Promise<SessionState> {
    inFlight ??= ask().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  async function signIn(username: string, password: string): Promise<SignInResult> {
    let res: Response;
    try {
      res = await fetch(SIGN_IN_URL, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ username, password }),
      });
    } catch {
      return { ok: false, error: NO_CONNECTION };
    }
    const body = await bodyOf(res);
    const user = res.ok ? userOf(body) : null;
    if (!user) return { ok: false, error: wordsOf(body) ?? SIGN_IN_FAILED };
    epoch++;
    state.value = { status: 'signed-in', user };
    return { ok: true };
  }

  async function signOut(): Promise<SignOutResult> {
    let res: Response;
    try {
      res = await fetch(SIGN_OUT_URL, { method: 'POST', headers: JSON_HEADERS, body: '{}' });
    } catch {
      return { ok: false, error: SIGN_OUT_FAILED };
    }
    // Every answer from the route cleared the cookie (routes/auth.ts), a 500 included.
    const body = res.ok ? null : await bodyOf(res);
    epoch++;
    state.value = { status: 'signed-out', ended: false };
    return res.ok ? { ok: true } : { ok: true, warning: wordsOf(body) ?? SIGN_OUT_FAILED };
  }

  function watch(): () => void {
    const page = globalThis.fetch;
    const watching: typeof fetch = async (input, init) => {
      const res = await page(input, init);
      if (res.status === 401 && state.peek().status === 'signed-in' && watched(input)) void check();
      return res;
    };
    globalThis.fetch = watching;

    const comeBack = () => {
      const now = state.peek().status;
      if (now === 'signed-in' || now === 'unreachable') void check();
    };
    const onVisibility = () => {
      if (win().document.visibilityState === 'visible') comeBack();
    };
    win().addEventListener('focus', comeBack);
    win().document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (globalThis.fetch === watching) globalThis.fetch = page;
      win().removeEventListener('focus', comeBack);
      win().document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  return { state, check, signIn, signOut, watch };
}
